import App from 'App';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import paths, { rootPaths } from './paths';

import ErrorLayout from '../layouts/ErrorLayout';
import MainLayout from '../layouts/MainLayout';

import Error404 from 'components/errors/Error404';
import Error500 from 'components/errors/Error500';

import AlertList from 'components/alerts/AlertList';
import CustomerDetails from 'components/app/support-desk/contact-details/CustomerDetails';
import Settings from 'components/app/support-desk/quick-links/Settings';
import TicketsPreview from 'components/app/support-desk/tickets-preview/TicketsPreview';
import AssetDetails from 'components/assets/AssetDetails';
import AssetEdit from 'components/assets/AssetEdit';
import AssetList from 'components/assets/AssetList';
import AssetRegistration from 'components/assets/AssetRegistration';
import Authorized from 'components/authentication/Authorized';
import CreatePasswordForm from 'components/authentication/CreatePasswordForm';
import InviteUserForm from 'components/authentication/InviteUserForm';
import Login from 'components/authentication/Login';
import PasswordResetForm from 'components/authentication/PasswordResetForm';
import ProtectedRoute from 'components/authentication/ProtectedRoute';
import SupportDesk from 'components/dashboards/support-desk';
import ArticleList from 'components/documents/ArticleList';
import ArticleSearch from 'components/documents/ArticleSearch';
import Error403 from 'components/errors/Error403';
import DattoRMMIntegration from 'components/integration/datto-rmm/DattoRMMIntegration';
import MSPTalksIntegration from 'components/integration/msptalks/MSPTalksIntegration';
import Starter from 'components/pages/Starter';
import MyCompany from 'components/pages/company/MyCompany';
import Landing from 'components/pages/landing/Landing';
import ProfileUser from 'components/pages/user/profile/ProfileUser';
import AgentRegistration from 'components/registration/AgentRegistration';
import AgentForm from 'components/registration/AgentForm';
import ApiKeysRegistration from 'components/registration/ApiKeysRegistration';
import ArticleEdit from 'components/registration/ArticleEdit';
import ArticlePreview from 'components/registration/ArticlePreview';
import ArticleRegistration from 'components/registration/ArticleRegistration';
import AssetTypeRegistration from 'components/registration/AssetTypeRegistration';
import BusinessScheduleList from 'components/registration/business-schedule/BusinessScheduleList';
import BusinessScheduleForm from 'components/registration/business-schedule/BusinessScheduleForm';
import ContactRegistration from 'components/registration/ContactRegistration';
import CustomerRegistration from 'components/registration/CustomerRegistration';
import EmailBoxRegistration from 'components/registration/EmailBoxRegistration';
import HolidayRegistration from 'components/registration/HolidayRegistration';
import PauseReasonRegistration from 'components/registration/PauseReasonRegistration';
import ProfileRegistration from 'components/registration/ProfileRegistration';
import ProfileEdit from 'components/registration/ProfileEdit';
import ScheduledTicketCloneRegistration from 'components/registration/ScheduledTicketCloneRegistration';
import ScheduledTicketEdit from 'components/registration/ScheduledTicketEdit';
import ScheduledTicketList from 'components/registration/ScheduledTicketList';
import ScheduledTicketRegistration from 'components/registration/ScheduledTicketRegistration';
import ServiceCatalogRegistration from 'components/registration/ServiceCatalogRegistration';
import ServiceGroupRegistration from 'components/registration/ServiceGroupRegistration';
import SlaPolicyEdit from 'components/registration/SlaPolicyEdit';
import TagsRegistration from 'components/registration/TagsRegistration';
import TaskTemplateClone from 'components/registration/TaskTemplateClone';
import TaskTemplateEdit from 'components/registration/TaskTemplateEdit';
import TaskTemplateNew from 'components/registration/TaskTemplateNew';
import TaskTemplateRegistration from 'components/registration/TaskTemplateRegistration';
import TypeTicketRegistration from 'components/registration/TypeTicketRegistration';
import ServiceCatalogDetails from 'components/registration/service-catalog-details/ServiceCatalogDetails';
import SlaPolicyList from 'components/registration/sla-policy-list/SlaPolicyList';
import CustomReportDetails from 'components/reports/custom/CustomReportDetails';
import CustomReportExecution from 'components/reports/custom/CustomReportExecution';
import CustomReportList from 'components/reports/custom/CustomReportList';
import CustomReportWizard from 'components/reports/custom/CustomReportWizard';
import ReportExecutiveCustomer from 'components/reports/executive/ReportExecutiveCustomer';
import ReportSupportDeskOverView from 'components/reports/overview/ReportSupportDeskOverView';
import ReportProductivity from 'components/reports/productivity/ReportProductivity';
import SearchResults from 'components/search/SearchResults';
import EmailNotification from 'components/settings/EmailNotification';
import EmailNotificationEdit from 'components/settings/EmailNotificationEdit';
import CatalogItems from 'components/settings/CatalogItems';
import MessageTemplates from 'components/settings/MessageTemplates';
import CustomFieldsSettings from 'components/settings/CustomFieldsSettings';
import BillingManagementPage from 'components/finance/BillingManagementPage';
import InvoiceDetailPage from 'components/finance/InvoiceDetailPage';
import ListPreTickets from 'components/tickets/ListPreTickets';
import ListTickets from 'components/tickets/ListTickets';
import AuthSimpleLayout from 'layouts/AuthSimpleLayout';

const routes = [
  {
    element: <App />,
    children: [
      {
        path: paths.starter,
        element: <Starter />
      },
      {
        path: paths.landing,
        element: <Landing />
      },
      {
        path: paths.resetPassword,
        element: <PasswordResetForm />
      },
      {
        path: paths.createPassword,
        element: <CreatePasswordForm />
      },
      {
        path: paths.inviteUser,
        element: <InviteUserForm />
      },
      {
        path: rootPaths.errorsRoot,
        element: <ErrorLayout />,
        children: [
          {
            path: paths.error404,
            element: <Error404 />
          },
          {
            path: paths.error403,
            element: <Error403 />
          },
          {
            path: paths.error500,
            element: <Error500 />
          }
        ]
      },
      {
        element: <AuthSimpleLayout />,
        children: [
          {
            path: paths.login,
            element: <Login />
          },
          {
            path: paths.authorized,
            element: <Authorized />
          }
        ]
      },
      {
        path: '/',
        element: <MainLayout />,
        children: [
          {
            path: paths.listPreTickets,
            element: (
              <ProtectedRoute>
                <ListPreTickets />
              </ProtectedRoute>
            )
          },
          {
            path: paths.settings,
            element: (
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            )
          },
          {
            path: paths.emailNotifications,
            element: (
              <ProtectedRoute>
                <EmailNotification />
              </ProtectedRoute>
            )
          },
          {
            path: paths.editEmailNotification,
            element: (
              <ProtectedRoute>
                <EmailNotificationEdit />
              </ProtectedRoute>
            )
          },
          {
            path: paths.searchResults,
            element: (
              <ProtectedRoute>
                <SearchResults />
              </ProtectedRoute>
            )
          },
          {
            path: rootPaths.dashboardRoot,
            children: [
              {
                path: paths.dashTickets,
                element: (
                  <ProtectedRoute>
                    <SupportDesk />
                  </ProtectedRoute>
                )
              }
            ]
          },
          {
            path: rootPaths.documents,
            children: [
              {
                path: paths.solutions,
                element: (
                  <ProtectedRoute>
                    <ArticleList />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.articleSearch,
                element: (
                  <ProtectedRoute>
                    <ArticleSearch />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.articleRegistration,
                element: (
                  <ProtectedRoute>
                    <ArticleRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.articlePreview,
                element: (
                  <ProtectedRoute>
                    <ArticlePreview />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.articleEdit,
                element: (
                  <ProtectedRoute>
                    <ArticleEdit />
                  </ProtectedRoute>
                )
              }
            ]
          },
          {
            path: rootPaths.ticketsRoot,
            children: [
              {
                path: paths.listTickets,
                element: (
                  <ProtectedRoute>
                    <ListTickets />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.ticketsPreview,
                element: (
                  <ProtectedRoute>
                    <TicketsPreview />
                  </ProtectedRoute>
                )
              }
            ]
          },
          {
            path: rootPaths.registrationRoot,
            children: [
              {
                path: paths.customerRegistration,
                element: (
                  <ProtectedRoute>
                    <CustomerRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.customerDetails,
                element: (
                  <ProtectedRoute>
                    <CustomerDetails />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.contactRegistration,
                element: (
                  <ProtectedRoute>
                    <ContactRegistration />
                  </ProtectedRoute>
                )
              }
              /* {
                path: paths.articleRegistration,
                element: <ProtectedRoute><ArticleRegistration /></ProtectedRoute>
              },
              {
                path: paths.articleEdit,
                element: <ProtectedRoute><ArticlePreview /></ProtectedRoute>
              }, */
            ]
          },
          {
            path: rootPaths.settings,
            children: [
              {
                path: paths.holidayRegistration,
                element: (
                  <ProtectedRoute>
                    <HolidayRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.emailBoxRegistration,
                element: (
                  <ProtectedRoute>
                    <EmailBoxRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.agentRegistration,
                element: (
                  <ProtectedRoute>
                    <AgentRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.agentCreate,
                element: (
                  <ProtectedRoute>
                    <AgentForm />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.agentEdit,
                element: (
                  <ProtectedRoute>
                    <AgentForm />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.serviceGroupRegistration,
                element: (
                  <ProtectedRoute>
                    <ServiceGroupRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.profileRegistration,
                element: (
                  <ProtectedRoute>
                    <ProfileRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.profileEdit,
                element: (
                  <ProtectedRoute>
                    <ProfileEdit />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.serviceCatalogRegistration,
                element: (
                  <ProtectedRoute>
                    <ServiceCatalogRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.serviceCatalogDetails,
                element: (
                  <ProtectedRoute>
                    <ServiceCatalogDetails />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.taskTemplateRegistration,
                element: (
                  <ProtectedRoute>
                    <TaskTemplateRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.taskTemplateEdit,
                element: (
                  <ProtectedRoute>
                    <TaskTemplateEdit />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.taskTemplateNew,
                element: (
                  <ProtectedRoute>
                    <TaskTemplateNew />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.taskTemplateClone,
                element: (
                  <ProtectedRoute>
                    <TaskTemplateClone />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.typeTicketRegistration,
                element: (
                  <ProtectedRoute>
                    <TypeTicketRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.slaPolicyList,
                element: (
                  <ProtectedRoute>
                    <SlaPolicyList />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.slaPolicyEdit,
                element: (
                  <ProtectedRoute>
                    <SlaPolicyEdit />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.businessHourRegistration,
                element: <Navigate to={paths.businessScheduleList} replace />
              },
              {
                path: paths.businessScheduleList,
                element: (
                  <ProtectedRoute>
                    <BusinessScheduleList />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.businessScheduleNew,
                element: (
                  <ProtectedRoute>
                    <BusinessScheduleForm />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.businessScheduleEdit,
                element: (
                  <ProtectedRoute>
                    <BusinessScheduleForm />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.pauseReasonRegistration,
                element: (
                  <ProtectedRoute>
                    <PauseReasonRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.scheduledTicketList,
                element: (
                  <ProtectedRoute>
                    <ScheduledTicketList />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.scheduledTicketRegistration,
                element: (
                  <ProtectedRoute>
                    <ScheduledTicketRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.scheduledTicketCloneRegistration,
                element: (
                  <ProtectedRoute>
                    <ScheduledTicketCloneRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.scheduledTicketEdit,
                element: (
                  <ProtectedRoute>
                    <ScheduledTicketEdit />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.assetTypeRegistration,
                element: (
                  <ProtectedRoute>
                    <AssetTypeRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.tagsRegistration,
                element: (
                  <ProtectedRoute>
                    <TagsRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.apiKeysRegistration,
                element: (
                  <ProtectedRoute>
                    <ApiKeysRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.catalogItems,
                element: (
                  <ProtectedRoute>
                    <CatalogItems />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.messageTemplates,
                element: (
                  <ProtectedRoute>
                    <MessageTemplates />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.customFieldsSettings,
                element: (
                  <ProtectedRoute>
                    <CustomFieldsSettings />
                  </ProtectedRoute>
                )
              }
            ]
          },
          {
            path: rootPaths.finance,
            children: [
              {
                path: paths.billing,
                element: (
                  <ProtectedRoute>
                    <BillingManagementPage />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.billingDetail,
                element: (
                  <ProtectedRoute>
                    <InvoiceDetailPage />
                  </ProtectedRoute>
                )
              }
            ]
          },
          {
            path: rootPaths.userRoot,
            children: [
              {
                path: paths.profileUser,
                element: (
                  <ProtectedRoute>
                    <ProfileUser />
                  </ProtectedRoute>
                )
              }
            ]
          },
          {
            path: rootPaths.companyRoot,
            children: [
              {
                path: paths.myCompany,
                element: (
                  <ProtectedRoute>
                    <MyCompany />
                  </ProtectedRoute>
                )
              }
            ]
          },
          {
            path: rootPaths.reports,
            children: [
              {
                path: paths.reportExecutiveCustomer,
                element: (
                  <ProtectedRoute>
                    <ReportExecutiveCustomer />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.productivityAnalysis,
                element: (
                  <ProtectedRoute>
                    <ReportProductivity />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.reportOverview,
                element: (
                  <ProtectedRoute>
                    <ReportSupportDeskOverView />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.customReportList,
                element: (
                  <ProtectedRoute>
                    <CustomReportList />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.customReportNew,
                element: (
                  <ProtectedRoute>
                    <CustomReportWizard />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.customReportEdit,
                element: (
                  <ProtectedRoute>
                    <CustomReportWizard />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.customReportDetails,
                element: (
                  <ProtectedRoute>
                    <CustomReportDetails />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.customReportExecution,
                element: (
                  <ProtectedRoute>
                    <CustomReportExecution />
                  </ProtectedRoute>
                )
              }
            ]
          },
          {
            path: rootPaths.integrations,
            children: [
              {
                path: paths.dattoRMM,
                element: (
                  <ProtectedRoute>
                    <DattoRMMIntegration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.mspTalks,
                element: (
                  <ProtectedRoute>
                    <MSPTalksIntegration />
                  </ProtectedRoute>
                )
              }
            ]
          },
          {
            path: rootPaths.assets,
            children: [
              {
                path: paths.assetList,
                element: (
                  <ProtectedRoute>
                    <AssetList />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.assetRegistration,
                element: (
                  <ProtectedRoute>
                    <AssetRegistration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.assetDetails,
                element: (
                  <ProtectedRoute>
                    <AssetDetails />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.assetEdit,
                element: (
                  <ProtectedRoute>
                    <AssetEdit />
                  </ProtectedRoute>
                )
              }
            ]
          },
          {
            path: rootPaths.alerts,
            children: [
              {
                path: paths.alertList,
                element: (
                  <ProtectedRoute>
                    <AlertList />
                  </ProtectedRoute>
                )
              }
            ]
          },
          {
            path: '*',
            element: <Navigate to={paths.error404} replace />
          }
        ]
      }
    ]
  }
];

export const router = createBrowserRouter(routes, {
  basename: process.env.PUBLIC_URL
});

export default routes;
